import React, { useEffect } from 'react'

/**
 * The terms of use, at /terms.
 *
 * The website has its own copy (website/src/pages/Terms.tsx), so each site can
 * show it without depending on the other. That copy is generated from this
 * file: after changing anything between the terms:start and terms:end markers
 * or the date, run `npm run sync-legal` in website/ (a website test fails until
 * you do). Like the privacy policy, it renders without a session so it can be
 * read before signing up.
 */

const LAST_UPDATED = '20 October 2026'
const CONTACT_EMAIL = 'privacy@sixpaths.co.uk'

function H2({ children }: { children: React.ReactNode }) {
  return <h2 className="font-display text-xl text-ink mt-10 mb-3">{children}</h2>
}

function List({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 space-y-1.5 my-3">{children}</ul>
}

const Contact = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent underline hover:no-underline">
    {CONTACT_EMAIL}
  </a>
)

export default function TermsOfUse() {
  useEffect(() => {
    const previous = document.title
    document.title = 'Terms of use · Snapshot Money'
    return () => {
      document.title = previous
    }
  }, [])

  return (
    <div className="min-h-[100dvh] overflow-y-auto">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14 text-[15px] leading-relaxed text-ink/80">
        <a href="/" className="text-sm text-accent hover:underline">
          ← Back to Snapshot Money
        </a>

        <h1 className="font-display text-3xl sm:text-4xl text-ink mt-6">Terms of use</h1>
        <p className="text-sm text-ink/70 mt-2">Last updated: {LAST_UPDATED}</p>

        {/* terms:start */}
        <p className="mt-6">
          These terms are the agreement between you and us for using Snapshot Money: the app at
          app.snapshotmoney.co.uk and the website at snapshotmoney.co.uk (together, the “Service”). By
          creating an account you agree to them, so please read them first. They sit alongside our{' '}
          <a href="/privacy" className="text-accent underline hover:no-underline">
            privacy policy
          </a>
          , which explains what we do with your data.
        </p>
        <p className="mt-3">
          The short version: Snapshot Money helps you plan your money, but it’s a calculator, not a
          financial adviser. It’s free. Your plan is yours. Use it fairly, and check anything important
          against your bank before you rely on it.
        </p>

        <H2>1. Who we are</H2>
        <p>
          Snapshot Money is a trading name of <strong>Six Paths Software Ltd</strong>, a company
          registered in England and Wales (company number 11987916) with its registered office at 5
          Whitehall Walk, St. Neots, PE19 2EE. “We”, “us” and “our” mean Six Paths Software Ltd. You can
          reach us at <Contact />.
        </p>

        <H2>2. Your account</H2>
        <List>
          <li>You must be 18 or over to create an account.</li>
          <li>
            An account is for one person. Use an email address you can receive mail at, since that’s how
            you confirm the account and reset your password.
          </li>
          <li>
            Keep your password to yourself. You’re responsible for what happens in your account, so if you
            think someone else has got into it, change your password and tell us.
          </li>
        </List>

        <H2>3. What Snapshot Money is, and what it isn’t</H2>
        <p>
          Snapshot Money works out future balances from what you put into it: your pay, bills, budgets
          and the rest. Its figures are projections, and they are only as good as what you enter.
        </p>
        <List>
          <li>
            <strong>It isn’t financial advice.</strong> Nothing in the Service is a recommendation to do
            anything with your money. We aren’t authorised or regulated by the Financial Conduct
            Authority, and the Service isn’t a regulated product.
          </li>
          <li>
            <strong>It doesn’t see your bank account.</strong> It doesn’t connect to your bank, so it
            can’t know about payments you haven’t entered or imported, or about changes your bank makes.
          </li>
          <li>
            <strong>Some things are worked out, not known.</strong> Working days, bank holidays, interest
            and loan payoffs follow common rules, and your bank’s may differ.
          </li>
        </List>
        <p>
          So before you make a decision that matters, such as a large payment or whether you can afford
          something, check the figures against your bank and, if you need it, get advice from a
          qualified adviser.
        </p>

        <H2>4. Price</H2>
        <p>
          The Service is free. We may add paid features in future, but we’ll never charge you anything
          unless you choose to sign up for one. Any paid feature will come with its own terms, which
          you’ll see before you pay.
        </p>

        <H2>5. Using the Service fairly</H2>
        <p>Please don’t:</p>
        <List>
          <li>use the Service for anything unlawful, or to store anything you don’t have the right to;</li>
          <li>
            try to get into other people’s accounts or our systems, or test, probe or get round our
            security;
          </li>
          <li>
            overload the Service, or use bots, scrapers or other automated means to access it, other than
            the app working as designed;
          </li>
          <li>
            put offensive, abusive or personal information into names you choose to share with other
            people (see Section 6); or
          </li>
          <li>copy, resell or offer the Service, or a version of it, as your own.</li>
        </List>

        <H2>6. Your content</H2>
        <p>
          Your plan, and everything else you put into the Service, stays yours. You give us permission to
          store it, copy it for backups, and process it as needed to run the Service for you, and for
          nothing else.
        </p>
        <p className="mt-3">
          If you leave sharing on, the names of shops and items you use, and (if you tick the box) your
          monthly spending totals, help build suggestions and averages that everyone sees. They never
          show who shared them. You give us permission to use what you share this way, and can stop
          sharing at any time. The privacy policy explains exactly what is shared and when.
        </p>
        <p className="mt-3">
          You can download everything we hold about you at any time from <em>Export data</em> in the
          account menu. It’s worth doing now and then, so you always have your own copy.
        </p>

        <H2>7. Keeping the Service running</H2>
        <p>
          We work to keep the Service available, secure and accurate, but we can’t promise it will always
          be available or free of mistakes. We may need to take it offline for maintenance, and we may
          change, add or remove features as it develops. If a change takes away something important to
          how you use it, we’ll tell you in advance where we can.
        </p>

        <H2>8. Our responsibility to you</H2>
        <p>
          We provide the Service with reasonable care and skill. If we don’t, and that causes you a loss
          that was foreseeable, we’re responsible for it. Nothing in these terms limits our responsibility
          for death or personal injury caused by our negligence, for fraud, or for anything else that the
          law doesn’t allow us to limit, and nothing affects your legal rights as a consumer.
        </p>
        <p className="mt-3">Within those limits:</p>
        <List>
          <li>
            The Service is for your own personal use. We aren’t responsible for business losses, such as
            lost profit or lost business opportunities.
          </li>
          <li>
            We aren’t responsible for decisions you make based on the Service’s projections, or for
            losses caused by information you entered being wrong or incomplete (see Section 3).
          </li>
          <li>
            We aren’t responsible for things outside our reasonable control, such as a failure of the
            internet or of a provider we depend on.
          </li>
        </List>
        <p>
          If something the Service supplies damages your device or other digital content because we
          didn’t use reasonable care and skill, we’ll either repair the damage or pay you fair
          compensation.
        </p>

        <H2>9. Ending your account</H2>
        <p>
          You can stop using the Service and delete your account at any time, from{' '}
          <em>Delete account</em> in the account menu. Deleting it removes your plan straight away, as the
          privacy policy describes.
        </p>
        <p className="mt-3">
          We may suspend or close your account if you seriously or repeatedly break these terms, or if the
          law requires us to. Unless the problem means we have to act at once, we’ll tell you first and
          give you the chance to put it right or export your data. If we ever decide to close the Service
          altogether, we’ll give you at least 60 days’ notice, so you have time to export your data.
        </p>

        <H2>10. Changes to these terms</H2>
        <p>
          We may update these terms, for example to reflect a new feature or a change in the law. If a
          change is significant, we’ll tell you by email or in the app at least 30 days before it takes
          effect. If you don’t agree to the new terms, you can delete your account before they start. The
          date at the top shows when these terms last changed.
        </p>

        <H2>11. The legal bits</H2>
        <List>
          <li>
            These terms are governed by the law of England and Wales. If you want to bring a claim, you
            can do so in the courts of England and Wales; if you live in Scotland or Northern Ireland, you
            can also bring it in your local courts.
          </li>
          <li>
            If a court decides that part of these terms can’t be enforced, the rest still applies.
          </li>
          <li>
            If we don’t enforce one of these terms straight away, we can still enforce it later.
          </li>
          <li>
            We may transfer this agreement to another organisation, for example if the business is sold,
            and will tell you if we do. Your rights under these terms won’t be reduced as a result.
          </li>
          <li>These terms are between you and us. Nobody else has any rights under them.</li>
        </List>

        <H2>12. Contact us</H2>
        <p>
          Questions about these terms, or a complaint about the Service: <Contact />.
        </p>
        {/* terms:end */}

        <p className="mt-12 pt-6 border-t border-line text-sm">
          <a href="/" className="text-accent hover:underline">
            ← Back to Snapshot Money
          </a>
        </p>
      </div>
    </div>
  )
}
